import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchBscAchievements } from "../../../api/companyApi";
import { useNotification } from "../../../providers/NotificationProvider";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BscAchievementListPage() {
  const { businessDomainId, businessUnitId, category, goalIdx } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}`;

  const config = BSC_CATEGORY_CONFIG[category];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [indicatorName, setIndicatorName] = useState("");

  const loadData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      // ① カテゴリのゴール一覧から対象指標名を取得
      const goals = await config.fetchGoals(businessUnitId);
      const goal = Array.isArray(goals) ? goals[Number(goalIdx)] : null;
      const name = goal?.indicator?.indicatorName || "";
      setIndicatorName(name);

      // ② 事業単位の実績一覧を取得し指標名でフィルタリング
      const achievements = await fetchBscAchievements(businessUnitId);
      const filtered = (Array.isArray(achievements) ? achievements : []).filter(
        (a) => a.goal?.indicator?.indicatorName === name
      );
      setRows(filtered.map((a, i) => ({ id: i, ...a })));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, category, goalIdx, config]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!config) {
    return <Typography color="error">Unknown BSC category: {category}</Typography>;
  }

  const columns = [
    {
      field: "indicatorName",
      headerName: t("bsc.indicator"),
      flex: 1,
      minWidth: 150,
      valueGetter: (v, row) => row?.goal?.indicator?.indicatorName || "",
    },
    {
      field: "unit",
      headerName: t("bsc.unit"),
      width: 80,
      valueGetter: (v, row) => row?.goal?.indicator?.unit || "",
    },
    {
      field: "plannedValue",
      headerName: t("bsc.plannedValue"),
      width: 110,
      valueGetter: (v, row) => row?.goal?.plannedValue ?? "",
    },
    {
      field: "actualValue",
      headerName: t("bsc.achievement.actualValue"),
      width: 110,
    },
    {
      field: "actualDate",
      headerName: t("bsc.achievement.actualDate"),
      width: 120,
      valueFormatter: (v) => formatDate(v),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          {t("bsc.achievement.list.title")}
          {indicatorName ? `（${indicatorName}）` : ""}
        </Typography>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/${goalIdx}`)}>
          {t("common.back")}
        </Button>
      </Box>
    </Box>
  );
}
